import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';
const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };
function KPI({ label, value, iconName, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const tt = { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

// Efficiency metrics use real data from operations-data endpoint

export default function DashOperations() {
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState({
    awaitingDocs: 0,
    pipelineByStage: [],
    agingLoans: [],
  });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/operations-data`, { headers });
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading…</div>
      </AdminLayout>
    );
  }

  const agingLoans = data.agingLoans || [];
  const pipelineByStage = data.pipelineByStage || [];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Operations</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Pipeline status &amp; processing efficiency</p>
        </div>

        {/* Stat Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <KPI label="Awaiting Docs" value={data.awaitingDocs} iconName="document" color="#ffdd86" />
          <KPI label="Active Pipeline" value={pipelineByStage.reduce((a, b) => a + (parseInt(b.count) || 0), 0)} iconName="chart" color="#3a6ad9" />
          <KPI label="Aging >14 Days" value={agingLoans.filter(l => (l.days_in_stage || l.daysInStage || 0) > 14).length} iconName="clock" color="#ff082f" />
          <KPI label="Pipeline Stages" value={pipelineByStage.length} iconName="chart" color="#acf44e" />
        </div>

        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Pipeline by Stage (real) */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Pipeline by Stage</h3>
            {pipelineByStage.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={pipelineByStage} layout="vertical" margin={{ top: 0, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="stage" type="category" tick={{ fontSize: 11 }} width={60} />
                  <Tooltip contentStyle={tt} />
                  <Bar dataKey="count" fill="#3a6ad9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No pipeline data yet</div>
            )}
          </div>

          {/* Operations Summary */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Operations Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Awaiting Documents', value: data.awaitingDocs || 0, note: 'Waiting on borrower uploads', color: data.awaitingDocs > 0 ? '#ffdd86' : '#acf44e' },
                { label: 'Aging Loans (>14d)', value: agingLoans.filter(l => (l.days_in_stage || l.daysInStage || 0) > 14).length, note: 'In current stage over 14 days', color: '#ff082f' },
              ].map((e) => (
                <div key={e.label} style={{ padding: '14px 16px', borderRadius: '10px', backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{e.label}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{e.note}</div>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: e.color }}>{e.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Aging Loans Table (real) */}
        <div style={card}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Aging Loans</h3>
          {agingLoans.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '32px 0' }}>No aging loans — pipeline looks healthy.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['Borrower', 'Stage', 'Days in Stage', 'Risk'].map((h) => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agingLoans.map((loan, i) => {
                  const days = loan.daysInStage || 0;
                  const risk = days > 30 ? 'High' : days > 14 ? 'Medium' : 'Low';
                  const riskColor = days > 30 ? '#ff082f' : days > 14 ? '#d97706' : '#acf44e';
                  const riskBg = days > 30 ? '#fff0f3' : days > 14 ? '#fffbeb' : '#f0fde4';
                  return (
                    <tr key={loan.id || i} style={{ borderBottom: i < agingLoans.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                      <td style={{ padding: '10px 12px', fontWeight: '500', color: '#111827' }}>{loan.borrower || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#374151' }}>{loan.stage || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: '600', color: riskColor }}>{days}d</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600', color: riskColor, backgroundColor: riskBg }}>{risk}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
